import React, { useState, useEffect } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { txtdb } from "../../firebase-config";
import { ImSpinner8 } from "react-icons/im";
import { useNavigate } from "react-router-dom";

// Country list with dialing codes
const countries = [
  { name: "United States", code: "+1" },
  { name: "United Kingdom", code: "+44" },
  { name: "Canada", code: "+1" },
  { name: "South Africa", code: "+27" },
  { name: "Germany", code: "+49" },
  { name: "France", code: "+33" },
  { name: "India", code: "+91" },
  { name: "Nigeria", code: "+234" },
  { name: "China", code: "+86" },
  { name: "Japan", code: "+81" },
  { name: "Mexico", code: "+52" },
  { name: "Ghana", code: "+233" },
  { name: "Kenya", code: "+254" },
  { name: "Australia", code: "+61" },
  { name: "Brazil", code: "+55" },
  { name: "Italy", code: "+39" },
  { name: "Spain", code: "+34" },
  { name: "UAE", code: "+971" },
  { name: "Turkey", code: "+90" },
  { name: "Netherlands", code: "+31" },
];

// Helpers for the custom date dropdowns
const days = Array.from({ length: 31 }, (_, i) => (i + 1).toString().padStart(2, "0"));
const months = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, "0"));
const currentYear = new Date().getFullYear();
// Start the year list from 18 years ago to gently enforce the adult rule visually
const years = Array.from({ length: 100 }, (_, i) => (currentYear - 18 - i).toString());

function Kyc() {
  const navigate = useNavigate();
  const auth = getAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    dob: "", // We will populate this from the custom dropdowns
    gender: "",
    country: "",
    dialCode: "",
    phone: "",
    idType: "",
    idNumber: "",
  });

  // Local state just for the custom date UI
  const [dateObj, setDateObj] = useState({ day: "", month: "", year: "" });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) setUser(currentUser);
      else navigate("/login");
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  // Handle standard input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "country") {
      const selected = countries.find((c) => c.name === value);
      setFormData({
        ...formData,
        country: value,
        dialCode: selected ? selected.code : "",
      });
    } else if (name === "phone") {
      const numericValue = value.replace(/[^0-9]/g, "");
      setFormData({ ...formData, [name]: numericValue });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // Handle custom date select changes
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    const updatedDate = { ...dateObj, [name]: value };
    setDateObj(updatedDate);

    // If all three are selected, stitch them into the standard format for formData
    if (updatedDate.day && updatedDate.month && updatedDate.year) {
      setFormData({ ...formData, dob: `${updatedDate.year}-${updatedDate.month}-${updatedDate.day}` });
    } else {
      setFormData({ ...formData, dob: "" }); // Keep blank if incomplete
    }
  };

  // check if age >= 18
  const isAdult = () => {
    if (!formData.dob) return false;
    const dob = new Date(formData.dob);
    const today = new Date();
    const age = today.getFullYear() - dob.getFullYear();
    const hasHadBirthday =
      today.getMonth() > dob.getMonth() ||
      (today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate());
    return age > 18 || (age === 18 && hasHadBirthday);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;

    if (!isAdult()) {
      setErrorMessage("You must be at least 18 years old to complete registration.");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      await setDoc(doc(txtdb, "kycInfo", user.uid), {
        ...formData,
        email: user.email,
        uid: user.uid,
        createdAt: new Date().toISOString(),
        status: "pending"
      });

      navigate("/verify-account/username");
    } catch (error) {
      console.error("Error saving KYC info:", error);
      setErrorMessage("Something went wrong, please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="kyc-page">
      <div className="kyc-container">
        <header className="kyc-header">
          <p className="small">ACCOUNT VERIFICATION</p>
          <h2>Personal Information</h2>
          <p className="big">Please provide your legal details accurately to secure your trading account.</p>
          <div className="accent-line"></div>
        </header>

        <form className="kyc-form" onSubmit={handleSubmit}>
          <div className="form-section">
            <h3 className="section-title">Applicant Data</h3>
            <div className="form-row">
              <input type="text" name="firstName" placeholder="First Name" value={formData.firstName} onChange={handleChange} required />
              <input type="text" name="lastName" placeholder="Last Name" value={formData.lastName} onChange={handleChange} required />
            </div>

            <div className="form-row">
              {/* THE NEW CUSTOM DATE GROUP */}
              <div className="custom-date-group">
                <select name="day" value={dateObj.day} onChange={handleDateChange} required title="Day">
                  <option value="" disabled>DD</option>
                  {days.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <select name="month" value={dateObj.month} onChange={handleDateChange} required title="Month">
                  <option value="" disabled>MM</option>
                  {months.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <select name="year" value={dateObj.year} onChange={handleDateChange} required title="Year">
                  <option value="" disabled>YYYY</option>
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>

              <select name="gender" value={formData.gender} onChange={handleChange} required>
                <option value="" disabled>Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Contact & Location</h3>
            <div className="form-row">
              <select name="country" value={formData.country} onChange={handleChange} required>
                <option value="" disabled>Select Country</option>
                {countries.map((c) => (
                  <option key={c.name} value={c.name}>{c.name}</option>
                ))}
              </select>

              <div className="phone-input-wrapper">
                <span className="dial-code-display">{formData.dialCode || "+00"}</span>
                <input type="tel" name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleChange} required />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Identity Verification</h3>
            <div className="form-row">
              <select name="idType" value={formData.idType} onChange={handleChange} required>
                <option value="" disabled>Select ID Type</option>
                <option value="National ID">National ID</option>
                <option value="Passport">Passport</option>
                <option value="Driver’s License">Driver’s License</option>
                <option value="Voter’s Card">Voter’s Card</option>
              </select>

              <input type="text" name="idNumber" placeholder="ID Number" value={formData.idNumber} onChange={handleChange} required />
            </div>
          </div>

          {errorMessage && <div className="error-message">{errorMessage}</div>}

          <div className="form-actions">
            <button className="submit" type="submit" disabled={loading}>
              {loading ? <ImSpinner8 className="spin" /> : "CONTINUE >"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Kyc;